@if (showGenerateForm() && selectedApplication(); as selected) {
  <div class="oa-billing-shell p-2 p-lg-3">
    <div class="hero-card mb-3">
      <div class="hero-copy">
        <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div class="d-flex align-items-center gap-3">
            <span class="icon-pill">
              <lucide-icon [img]="FileTextIcon" [size]="16"></lucide-icon>
            </span>
            <div>
              <h1 class="m-0 page-title">Generate OA Bills</h1>
              <p class="page-subtitle mb-0">Configure buyer and seller charge allocation for the selected OA application.</p>
            </div>
          </div>
          <button type="button" class="action-btn secondary-btn" (click)="closeGenerateForm()">
            <lucide-icon [img]="MoveLeftIcon" [size]="14"></lucide-icon>
            <span>Back to Billing</span>
          </button>
        </div>
      </div>
    </div>

    <div class="form-panel mb-3">
      <div class="form-panel-head">Selected Applications (1)</div>
      <div class="selected-app-card">
        <div>
          <div class="fw-bold">{{ selected.applicationId }}</div>
          <div>
            <span class="text-primary fw-semibold">{{ selected.buyer }}</span>
            <span class="mx-1">↔</span>
            <span class="text-success fw-semibold">{{ selected.seller }}</span>
          </div>
        </div>
        <div class="text-end">
          <div class="fw-bold">{{ selected.quantum }} MWh</div>
          <div class="text-muted">{{ selected.source }}</div>
        </div>
      </div>
    </div>

    <div class="form-panel mb-3">
      <div class="form-panel-head">Charge Allocation Configuration</div>
      <div class="table-responsive">
        <table class="table billing-table mb-0 charge-table">
          <thead>
            <tr>
              <th>Charge Type</th>
              <th>Total Amount</th>
              <th>Paid By</th>
              <th>Buyer Share</th>
              <th>Seller Share</th>
            </tr>
          </thead>
          <tbody>
            @for (row of chargeRows(); track row.chargeType; let idx = $index) {
              <tr>
                <td>{{ row.chargeType }}</td>
                <td class="fw-semibold">{{ formatCurrency(row.totalAmount) }}</td>
                <td>
                  <select class="form-select form-select-sm" [ngModel]="row.paidBy" (ngModelChange)="onPaidByChange(idx, $event)">
                    <option value="Both">Both</option>
                    <option value="Buyer">Buyer</option>
                    <option value="Seller">Seller</option>
                  </select>
                </td>
                <td>
                  <span class="share-pill">{{ row.buyerPercent }} %</span>
                  <span class="share-pill amount">{{ formatCurrency(buyerAmount(row)) }}</span>
                </td>
                <td>
                  <span class="share-pill">{{ row.sellerPercent }} %</span>
                  <span class="share-pill amount">{{ formatCurrency(sellerAmount(row)) }}</span>
                </td>
              </tr>
            }
            <tr class="total-row">
              <td>TOTAL</td>
              <td>{{ formatCurrency(totalAmount()) }}</td>
              <td>-</td>
              <td>{{ formatCurrency(buyerTotal()) }}</td>
              <td>{{ formatCurrency(sellerTotal()) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-lg-6">
        <div class="summary-card-box buyer-box">
          <div class="summary-head">Buyer Bills <small>Per application</small></div>
          <ul>
            @for (row of chargeRows(); track row.chargeType) {
              <li>
                <span>{{ row.chargeType }} ({{ row.buyerPercent }}%)</span>
                <span>{{ formatCurrency(buyerAmount(row)) }}</span>
              </li>
            }
          </ul>
          <div class="summary-total">Total/bill: <strong>{{ formatCurrency(buyerTotal()) }}</strong></div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="summary-card-box seller-box">
          <div class="summary-head">Seller Bills <small>Per application</small></div>
          <ul>
            @for (row of chargeRows(); track row.chargeType) {
              <li>
                <span>{{ row.chargeType }} ({{ row.sellerPercent }}%)</span>
                <span>{{ formatCurrency(sellerAmount(row)) }}</span>
              </li>
            }
          </ul>
          <div class="summary-total">Total/bill: <strong>{{ formatCurrency(sellerTotal()) }}</strong></div>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <button type="button" class="action-btn secondary-btn" (click)="closeGenerateForm()">Cancel</button>
      <button type="button" class="action-btn primary-btn" (click)="generateBill()">Generate Bill</button>
    </div>
  </div>
} @else {
  <div class="oa-billing-shell p-2 p-lg-3">
    <div class="hero-card mb-3">
      <div class="hero-copy">
        <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div class="d-flex align-items-center gap-3">
            <span class="icon-pill">
              <lucide-icon [img]="FileTextIcon" [size]="16"></lucide-icon>
            </span>
            <div>
              <h1 class="m-0 page-title">OA Billing Management</h1>
              <p class="page-subtitle mb-0">Manage billing for approved OA applications, generated invoices, and merge-ready combinations.</p>
            </div>
          </div>
          <button
            type="button"
            class="action-btn success-btn"
            (click)="openGenerateFormForSelection()"
          >
            <lucide-icon [img]="PlusIcon" [size]="14"></lucide-icon>
            <span>Generate Bills</span>
          </button>
        </div>
      </div>
    </div>

    <div class="stats-grid mb-3">
      @for (card of stats(); track card.label) {
        <div class="metric-card">
          <div class="metric-head">{{ card.label }}</div>
          <div class="metric-value">{{ card.value }}</div>
          <div class="metric-foot">
            @if (card.key === 'apps') {
              Approved OA applications in billing queue
            } @else if (card.key === 'unbilled') {
              Applications ready for bill generation
            } @else if (card.key === 'totalAmount') {
              Total value of generated OA bills
            } @else if (card.key === 'pendingAmount') {
              Total value awaiting OA bill generation
            } @else {
              Bills eligible for merge action
            }
          </div>
          <span class="metric-icon">
            <lucide-icon [img]="cardIcon(card)" [size]="18"></lucide-icon>
          </span>
        </div>
      }
    </div>

    <div class="panel-wrap">
      <div class="tab-switcher" role="tablist" aria-label="OA Billing Tabs">
        <button
          type="button"
          class="tab-btn"
          [class.active]="activeTab() === 'unbilled'"
          (click)="setTab('unbilled')"
        >
          <lucide-icon [img]="CircleHelpIcon" [size]="14"></lucide-icon>
          Unbilled
          <span class="pill">{{ tabCount().unbilled }}</span>
        </button>
        <button
          type="button"
          class="tab-btn"
          [class.active]="activeTab() === 'bills'"
          (click)="setTab('bills')"
        >
          <lucide-icon [img]="FileTextIcon" [size]="14"></lucide-icon>
          Bills
          <span class="pill">{{ tabCount().bills }}</span>
        </button>
        <button
          type="button"
          class="tab-btn"
          [class.active]="activeTab() === 'mergeable'"
          (click)="setTab('mergeable')"
        >
          <lucide-icon [img]="CopyCheckIcon" [size]="14"></lucide-icon>
          Mergeable
          <span class="pill">0</span>
          <!-- {{ tabCount().mergeable }} -->
        </button>
      </div>

      <div class="toolbar-actions toolbar-actions--inline">
        <div class="toolbar-copy">
          <div class="toolbar-title">{{ alertTitle() }}</div>
          <div class="toolbar-subtitle">{{ alertSubtitle() }}</div>
        </div>
        @if (activeTab() === 'unbilled') {
          <button type="button" class="action-btn secondary-btn" (click)="toggleSelectAllUnbilledRows()">
            {{ isUnbilledSelectAllActive() ? 'Deselect All' : 'Select All' }}
          </button>
        }
      </div>
    </div>

    <div class="selection-strip mb-0">
      <div class="selection-copy">
        <strong>{{ tabCount()[activeTab()] }}</strong>
        @if (activeTab() === 'unbilled') {
          application{{ tabCount()[activeTab()] === 1 ? '' : 's' }} waiting for OA bill generation
          <span class="ms-2">| <strong>{{ selectedUnbilledCount() }}</strong> selected</span>
        } @else if (activeTab() === 'bills') {
          OA bill{{ tabCount()[activeTab()] === 1 ? '' : 's' }} available in the generated register
        } @else {
          buyer-seller combination{{ tabCount()[activeTab()] === 1 ? '' : 's' }} eligible for merge
        }
      </div>
      <div class="selection-note">
        @if (activeTab() === 'unbilled') {
          Select All only checks unbilled rows that share the same buyer, seller, buyer LOA, seller LOA, and bill cycle.
        } @else {
          Use the tabs to switch between pending applications, generated bills, and merge-ready records.
        }
      </div>
    </div>

    <div class="table-wrap">
      <div class="table-responsive">
        <table class="table billing-table mb-0">
          <thead class="sticky-top">
            <tr>
              <th style="width:60px"></th>
              <th style="min-width:170px">Application ID</th>
              <th style="min-width:160px">Contract No</th>
              <th style="min-width:140px">Type</th>
              <th style="min-width:140px">Seller</th>
              <th style="min-width:170px">Buyer</th>
              <th style="min-width:180px">Generation Source</th>
              <th style="min-width:140px">Invoice For</th>
              <th style="min-width:130px">Invoice Date</th>
              <th style="min-width:140px">Amount</th>
              <th style="min-width:180px">Status</th>
              <th style="min-width:190px">Action</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr>
                <td colspan="12" class="text-center py-4 text-muted fw-semibold">Loading...</td>
              </tr>
            } @else if (activeTab() === 'bills') {
              @if (generatedBills().length === 0) {
                <tr>
                  <td colspan="12" class="text-center py-4 text-muted fw-semibold">{{ tableEmptyMessage() }}</td>
                </tr>
              } @else {
                @for (bill of pagedGeneratedBills(); track bill.rowKey) {
                  <tr>
                    <td></td>
                    <td>{{ bill.applicationId }}</td>
                    <td>
                      <button type="button" class="table-link-btn" (click)="openContract(bill)">{{ bill.contractId }}</button>
                    </td>
                    <td><span class="type-chip" [ngClass]="typeClass(bill.type)">{{ bill.type }}</span></td>
                    <td class="text-success fw-medium">{{ bill.seller }}</td>
                    <td class="text-primary fw-medium">{{ bill.buyer }}</td>
                    <td><span class="source-chip">{{ bill.billId }}</span></td>
                    <td><span class="type-chip type-monthly">{{ bill.party }}</span></td>
                    <td>{{ bill.invoiceDate }}</td>
                    <td class="fw-semibold">{{ formatCurrency(bill.totalAmount) }}</td>
                    
                    <td>Generated</td>
                    <td>
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <button type="button" class="icon-btn" title="Download Bill" (click)="downloadGeneratedBill(bill)">
                          <lucide-icon [img]="DownloadIcon" [size]="14"></lucide-icon>
                        </button>
                      </div>
                    </td>
                  </tr>
                }
              }
            } @else if (activeTab() !== 'unbilled') {
              <tr>
                <td colspan="12" class="text-center py-4 text-muted fw-semibold">{{ tableEmptyMessage() }}</td>
              </tr>
            } @else if (unbilledRows().length === 0) {
              <tr>
                <td colspan="12" class="text-center py-4 text-muted fw-semibold">{{ tableEmptyMessage() }}</td>
              </tr>
            } @else {
              @for (row of pagedUnbilledRows(); track row.id) {
                <tr>
                  <td>
                    <input
                      type="checkbox"
                      [checked]="isUnbilledRowSelected(row.id)"
                      (change)="toggleUnbilledRowSelection(row.id, $any($event.target).checked)"
                    />
                  </td>
                  <td>
                    <div class="id-tooltip-wrap" tabindex="0">
                      <span>{{ row.applicationId }}</span>
                      <div class="id-tooltip">
                        <div class="id-tooltip-header">
                          <div class="id-tooltip-title">Application Details</div>
                          <div class="id-tooltip-subtitle">{{ row.applicationId }}</div>
                        </div>
                        <div class="id-tooltip-grid">
                          <div class="id-tooltip-item">
                            <span class="id-tooltip-label">Contract</span>
                            <span class="id-tooltip-value">{{ row.contractId }}</span>
                          </div>
                          <div class="id-tooltip-item">
                            <span class="id-tooltip-label">Source</span>
                            <span class="id-tooltip-value">{{ row.source }}</span>
                          </div>
                          <div class="id-tooltip-item">
                            <span class="id-tooltip-label">Route From</span>
                            <span class="id-tooltip-value">{{ row.routeFrom }}</span>
                          </div>
                          <div class="id-tooltip-item">
                            <span class="id-tooltip-label">Route To</span>
                            <span class="id-tooltip-value">{{ row.routeTo }}</span>
                          </div>
                          <div class="id-tooltip-item id-tooltip-item--full">
                            <span class="id-tooltip-label">Buyer Address</span>
                            <span class="id-tooltip-value">{{ row.buyerAddress }}</span>
                          </div>
                          <div class="id-tooltip-item id-tooltip-item--full">
                            <span class="id-tooltip-label">Seller Address</span>
                            <span class="id-tooltip-value">{{ row.sellerAddress }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <button type="button" class="table-link-btn" (click)="openContract(row)">{{ row.contractId }}</button>
                  </td>
                  <td><span class="type-chip" [ngClass]="typeClass(row.type)">{{ row.type }}</span></td>
                  <td class="text-success fw-medium">{{ row.seller }}</td>
                  <td class="text-primary fw-medium">{{ row.buyer }}</td>
                  <td><span class="source-chip">{{ row.source }}</span></td>
                  <td><span class="type-chip type-monthly">Buyer / Seller</span></td>
                  <td>-</td>
                  <td class="fw-semibold">{{ row.approvalAvailable ? formatCurrency(unbilledAmount(row)) : '-' }}</td>
                  <td>{{ row.approvalAvailable ? 'Not Generated' : 'Waiting for Approval' }}</td>
                  
                  <td>
                    <div class="action-tooltip-wrap" tabindex="0">
                      <button
                        type="button"
                        class="action-btn primary-btn action-btn--sm"
                        [disabled]="!row.approvalAvailable"
                        [attr.aria-label]="generateBillDisabledReason(row)"
                        (click)="openGenerateForm(row)"
                      >
                        Generate Bills
                      </button>
                      @if (!row.approvalAvailable) {
                        <div class="action-tooltip">{{ generateBillDisabledReason(row) }}</div>
                      }
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      @if (activeTab() !== 'mergeable') {
        <div class="pager-row">
          <div class="pager-meta">{{ pageStart() }}-{{ pageEnd() }} of {{ totalRows() }} {{ activeTab() === 'bills' ? 'bills' : 'applications' }}</div>
          <div class="pagination-shell">
            <button class="pager-btn" [disabled]="currentPageSafe() <= 1" (click)="currentPage.set(1)" aria-label="First page">
              <lucide-icon [img]="ChevronsLeftIcon" [size]="14"></lucide-icon>
            </button>
            <button class="pager-btn" [disabled]="currentPageSafe() <= 1" (click)="goToPreviousPage()" aria-label="Previous page">
              <lucide-icon [img]="ChevronLeftIcon" [size]="14"></lucide-icon>
            </button>
            <span class="page-indicator current">{{ currentPageSafe() }}</span>
            <button class="pager-btn" [disabled]="currentPageSafe() >= totalPages()" (click)="goToNextPage()" aria-label="Next page">
              <lucide-icon [img]="ChevronRightIcon" [size]="14"></lucide-icon>
            </button>
            <button class="pager-btn" [disabled]="currentPageSafe() >= totalPages()" (click)="currentPage.set(totalPages())" aria-label="Last page">
              <lucide-icon [img]="ChevronsRightIcon" [size]="14"></lucide-icon>
            </button>
            <select class="form-select form-select-sm pager-size-select" [ngModel]="pageSize()" (ngModelChange)="onPageSizeChange(($event || '10').toString())" aria-label="Rows per page">
              @for(page of pageSizeOptions; track page){
                <option [value]="page">{{page}}/Page</option>
              }
            </select>
          </div>
        </div>
      }
    </div>
  </div>
}
